import {
  Button,
  Chip,
  ChipGroup,
  Icon,
  Input,
  Modal,
  PageHeader,
  Switch,
  Table,
  TableColumn,
} from '@components';
import { useModal } from '@hooks';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import BudgetTemplateAPICaller, {
  BudgetTemplateFilters,
} from '@services/api/budgetTemplate';
import { BudgetTemplate } from 'interfaces/BudgetTemplate';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { monthsChipsEnum } from 'enums';

// Same convention as the API's RESERVED_BUDGET_TEMPLATE_SUFFIX: templates
// auto-generated by the Budget despesas grid's legacy sync bridge. The
// backend already refuses to edit/delete these — this only decides how the
// (already-blocked) UI presents them.
const RESERVED_TEMPLATE_SUFFIX = ' Auto';
const isReservedTemplate = (name?: string) =>
  !!name?.endsWith(RESERVED_TEMPLATE_SUFFIX);

export default function BudgetTemplatePage() {
  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<BudgetTemplate>;
  }>({
    total: 0,
    result: [],
  });
  const [filters, setFilters] = useState<BudgetTemplateFilters>({});

  const { createOrUpdate, fetch, remove } = BudgetTemplateAPICaller;
  const { isOpen, toggle: toggleBudgetTemplateModal } = useModal();
  const [allMonthChips, setAllMonthChips] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [monthChips, setMonthChips] = useState(monthsChipsEnum);

  const confirmation = useConfirmationModal();
  const {
    register,
    setError,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    loadTableData(filters);
  }, [filters]);

  const loadTableData = (filters: BudgetTemplateFilters) => {
    setIsLoading(true);
    BudgetTemplateAPICaller.listAll(filters)
      .then((values) => setTableData(values))
      .finally(() => setIsLoading(false));
  };

  const setBudgetValues = async (budgetId: string, active?: boolean) => {
    const budgetTemplate = await fetch(budgetId);

    setValue('id', budgetTemplate.id);
    setValue('name', budgetTemplate.name);
    setValue('year', budgetTemplate.year);
    setValue('active', active !== undefined ? active : budgetTemplate.active);
    setValue('months', budgetTemplate.months);

    setMonthChips((prevMonthChips) => {
      return prevMonthChips.map((monthChip) => {
        return {
          ...monthChip,
          active: budgetTemplate.months.includes(monthChip.key),
        };
      });
    });
  };

  const openEditModal = async (editId: string) => {
    setBudgetValues(editId);
    toggleBudgetTemplateModal();
  };

  const handleRemoveBudgetTemplate = async (removeId: string) => {
    confirmation({
      title: 'Excluir modelo de bugdet',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir esse modelo de budget?',
      onSubmit: () =>
        remove(removeId)
          .then((_) => loadTableData(filters))
          .catch((error) => {
            const message = error.response.data.message || 'Erro ao excluir!';
            toast.error(message);
          }),
    });
  };

  const activeOrInactiveBudgetTemplate = async (
    budgetId: string,
    active: boolean
  ) => {
    setBudgetValues(budgetId, active).then(() => {
      const budgetTemplate = getValues() as BudgetTemplate;
      createOrUpdate(
        {
          ...budgetTemplate,
          active,
        },
        setError
      );
    });
  };

  const handleCreateOrUpdate = async (data: BudgetTemplate) => {
    if (!data.id) {
      data.active = true;
    }
    createOrUpdate(data, setError).then((res) => {
      if (res.data.errors) return;
      toggleBudgetTemplateModal();
      loadTableData(filters);
      setValue('months', []);
    });
  };

  const handleChipClick = (index: number) => {
    let newMonthChips = monthChips.map((monthChip, monthChipIndex) => {
      if (monthChipIndex !== index) return monthChip;

      return {
        ...monthChip,
        active: !monthChip.active,
      };
    });

    setMonthChips(newMonthChips);
    setValue(
      'months',
      newMonthChips
        .filter((monthChip) => monthChip.active)
        .map((monthChip) => monthChip.key)
    );
  };

  const handleAllMonthChipsClick = () => {
    setAllMonthChips(!allMonthChips);
    setMonthChips(
      monthChips.map((monthChip) => {
        monthChip.active = !allMonthChips;
        return monthChip;
      })
    );

    setValue(
      'months',
      monthChips
        .filter((monthChip) => monthChip.active)
        .map((monthChip) => monthChip.key)
    );
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Modelo de budget" />

        <div className="mt-s-200">
          <Table
            dataKey="id"
            data={tableData.result}
            totalRecords={tableData.total}
            showColumnSelector={false}
            withPagination
            withSearchInput
            isLoading={isLoading}
            filters={filters}
            setFilters={setFilters}
            additionalFilters={
              <div className="d-flex justify-end">
                <Button
                  className="ml-s-100"
                  suffixes={<Icon>add</Icon>}
                  design="filled"
                  onClick={() => {
                    setValue('id', undefined);
                    setValue('name', '');
                    setValue('year', '');
                    setValue('months', []);
                    return toggleBudgetTemplateModal();
                  }}
                >
                  Novo modelo de budget
                </Button>
              </div>
            }
          >
            <TableColumn
              fromKey="name"
              header="Modelo de budget"
              setFilters={setFilters}
              render={(name) => (
                <>
                  {name}
                  {isReservedTemplate(name) && (
                    <span
                      className="status-badge"
                      title="Gerado automaticamente pelo grid de Budget despesas — não pode ser editado ou apagado aqui"
                      style={{
                        marginLeft: 8,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: '#eef2f8',
                        color: '#1f3a5f',
                      }}
                    >
                      Reservado
                    </span>
                  )}
                </>
              )}
            />
            <TableColumn fromKey="year" header="Ano" />
            <TableColumn
              fromKey="id"
              header="Status"
              render={(id, row) => {
                return (
                  <Switch
                    label=""
                    defaultChecked={row.active}
                    disabled={isReservedTemplate(row.name)}
                    onChange={() => {
                      activeOrInactiveBudgetTemplate(id, !row.active);
                    }}
                  />
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id, row) => {
                const reserved = isReservedTemplate(row.name);
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    disabled={reserved}
                    title={
                      reserved
                        ? 'Modelo reservado — gerenciado automaticamente'
                        : undefined
                    }
                    onClick={() => {
                      if (reserved) return;
                      openEditModal(id);
                    }}
                  />
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Deletar"
              disableSorting
              render={(id, row) => {
                const reserved = isReservedTemplate(row.name);
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>delete</Icon>}
                    disabled={reserved}
                    title={
                      reserved
                        ? 'Modelo reservado — gerenciado automaticamente'
                        : undefined
                    }
                    onClick={async () => {
                      if (reserved) return;
                      handleRemoveBudgetTemplate(id);
                    }}
                  />
                );
              }}
            />
          </Table>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClickAway={toggleBudgetTemplateModal}
        size="medium"
        title="Cadastrar modelo budget"
      >
        <form
          onSubmit={handleSubmit((data) => {
            data.months = monthChips
              .filter((monthChip) => monthChip.active)
              .map((monthChip) => {
                return monthChip.key;
              });

            handleCreateOrUpdate(data as BudgetTemplate);
          })}
        >
          <div className="row gap-x-2 mb-s-200">
            <div className="col-sm-6">
              <Input
                label="Nome"
                error={!!errors.name}
                caption={errors.name?.message as string}
                placeholder="Digite o nome do budget"
                form={register('name', { required: 'Obrigatório' })}
              />
            </div>

            <div className="col-sm-6">
              <Input
                className="mb-s-400"
                label="Ano"
                error={!!errors.year}
                caption={errors.year?.message as string}
                placeholder="Digite o ano"
                mask="onlyNumbers"
                form={register('year', {
                  required: 'Obrigatório',
                  maxLength: 4,
                  pattern: {
                    value: /^[0-9]*$/,
                    message: 'Somente números',
                  },
                })}
              />
            </div>
          </div>

          <label className="form-input__label">Mês</label>
          <div className="row gap-x-2 mb-s-200">
            <div className="col-sm-12">
              <div className="d-flex overflow-x-auto">
                <Chip
                  key={'all'}
                  size="small"
                  className={`mr-s-100 ${allMonthChips ? 'chip-active' : ''}`}
                  onClick={() => handleAllMonthChipsClick()}
                >
                  Todos
                </Chip>

                <ChipGroup
                  chips={monthChips}
                  onChipClick={handleChipClick}
                  sizeChips="small"
                  error={!!errors.months}
                  caption={errors.months?.message as string}
                  form={register('months', { required: 'Obrigatório' })}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-end">
            <Button className="pl-s-900 pr-s-900" type="submit">
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
